import { COMPONENT_VALUE_TYPES as types, DIRECTIONS } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  editor?: object;
  background?: {
    padding?: string;
    useBackgroundColor?: boolean;
    backgroundColor?: string;
    desktopImage?: string;
    tabletImage?: string;
    mobileImage?: string;
  };
  blocks?: {
    title?: string;
    description?: string;
  }[];
  blockTitleColor?: string;
  blockDescriptionColor?: string;
  dividerColor?: string;
  animation?: {
    enabled?: boolean;
    type?: 'FADE' | 'SLIDE' | 'SCALE';
    direction?: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
    duration?: number | string;
    initialDelay?: number | string;
    stagger?: number | string;
    animateHeader?: boolean;
    animateBlocks?: boolean;
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'background',
    label: 'Background',
    type: types.OBJECT,
    children: [
      {
        key: 'padding',
        label: 'Padding',
        type: types.TEXT,
      },
      {
        key: 'useBackgroundColor',
        label: 'Use Background Color',
        type: types.BOOLEAN,
      },
      {
        key: 'backgroundColor',
        label: 'Background Color',
        type: types.COLOR_PICKER,
      },
      {
        key: 'desktopImage',
        label: 'Desktop Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'tabletImage',
        label: 'Tablet Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'mobileImage',
        label: 'Mobile Background Image',
        type: types.S3_ASSET_URL,
      },
    ],
  },
  {
    key: 'blocks',
    label: 'Blocks',
    type: types.ARRAY,
    children: [
      {
        key: 'title',
        label: 'Block Title',
        type: types.TEXT,
      },
      {
        key: 'description',
        label: 'Block Description',
        type: types.TEXTAREA,
        options: {
          rows: 4,
        } as any,
      },
    ],
  },
  {
    key: 'blockTitleColor',
    label: 'Block Title Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'blockDescriptionColor',
    label: 'Block Description Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'dividerColor',
    label: 'Block Divider Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'animation',
    label: 'Animation',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enable Animation',
        type: types.BOOLEAN,
      },
      {
        key: 'type',
        label: 'Animation Type',
        type: types.ENUM,
        options: ['FADE', 'SLIDE', 'SCALE'],
      },
      {
        key: 'direction',
        label: 'Direction (for Slide)',
        type: types.ENUM,
        options: DIRECTIONS,
      },
      {
        key: 'duration',
        label: 'Duration (s)',
        type: types.TEXT,
      },
      {
        key: 'initialDelay',
        label: 'Initial Delay (s)',
        type: types.TEXT,
      },
      {
        key: 'stagger',
        label: 'Stagger Delay (s)',
        type: types.TEXT,
        tooltip: 'Delay between each item\'s animation start.',
      },
      {
        key: 'animateHeader',
        label: 'Animate Header',
        type: types.BOOLEAN,
      },
      {
        key: 'animateBlocks',
        label: 'Animate Content',
        type: types.BOOLEAN,
      },
    ],
  },
];

const defaultProps: Props = {
  background: {
    padding: '100px 15%',
    useBackgroundColor: false,
    backgroundColor: '#000000',
    desktopImage: 'https://genesis-cdn.ff.com/cxc10/card-section-default-bg-2-default.jpg',
    tabletImage: '',
    mobileImage: '',
  },
  blocks: [
    {
      title: 'AI Market Insights',
      description: 'Leverages advanced models, on-chain data, and quantitative signals to analyze market structure in real time, capture trends and anomalies, and generate forward-looking opportunity insights.',
    },
    {
      title: 'Multi-Platform Aggregated Trading',
      description: 'Integrates quotes and liquidity from CEXs and DEXs, automatically plans optimal routes, reduces slippage and costs, and delivers a consistent and stable trading experience.',
    },
    {
      title: 'AI Trading Agent',
      description: 'Autonomous AI Agents continuously execute strategies, rebalance positions, and manage risk. Portfolios are automatically managed based on user preferences, enabling fully end-to-end intelligent trading.',
    },
    {
      title: 'AI Smart Copy-Trading',
      description: 'Evaluates the performance of traders and AI models to build a replicable strategy execution system, while dynamic risk control ensures robust copy-trading results.',
    },
    {
      title: 'AI Decision Support',
      description: 'Generates personalized strategies, risk alerts, and position optimization recommendations based on user profiles and market dynamics, using data-driven insights to improve decision quality.',
    },
  ],
  blockTitleColor: 'rgba(198, 170, 132, 1)',
  blockDescriptionColor: 'rgba(171, 171, 171, 1)',
  dividerColor: 'rgba(198, 170, 132, 1)',
  animation: {
    enabled: false,
    type: 'SLIDE',
    direction: 'UP',
    duration: 0.8,
    initialDelay: 0,
    stagger: 0.2,
    animateHeader: true,
    animateBlocks: true,
  },
};

export default {
  title: 'C10 Vertical Blocks',
  id: 'C10VerticalBlocks',
  propsSchema,
  defaultProps,
}
